<script setup lang="ts">
import { computed } from 'vue';
import {
  FeaturedResourceCardProps,
  FeaturedResourceCardResourceFields,
} from '@/types/components/FeaturedResourceCard';
import utils from '@/utils';

const props = defineProps<FeaturedResourceCardProps>();

const fields = computed(() => props.fields);

const rootClass = computed(() =>
  utils.overall.baseComClass(props.fields, 'c-featured-resource-card')
);
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const resources = computed(() => fields.value?.resources || []);
const heroImage = computed(() => {
  const image = fields.value?.image as any;
  return Array.isArray(image) ? image[0] : image;
});
const resourceCountLabel = computed(() => {
  const count = resources.value.length;
  const suffix = fields.value?.resourceCountSuffix?.value || 'items';
  return `${count} ${count === 1 && suffix === 'items' ? 'item' : suffix}`;
});

const getResourceTitle = (resource: FeaturedResourceCardResourceFields) =>
  resource.title?.value || '';

const getLinkHref = (link: any) => link?.value?.href || link?.href || '#';

const getLinkAttrs = (link: any) => {
  const value = link?.value || link || {};
  const href = getLinkHref(link);
  const attrs: Record<string, string> = { href };

  if (value.target) attrs.target = value.target;
  if (value.title) attrs.title = value.title;
  if (value.target === '_blank') attrs.rel = 'noopener noreferrer';

  return attrs;
};
</script>

<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[1, 22]" :mobile-default-grid="[1, 14]">
      <div class="c-featured-resource-card__hero">
        <div class="c-featured-resource-card__hero-copy">
          <h2 v-if="fields?.title?.value" v-html="fields?.title.value" />
          <JssRichText class="c-featured-resource-card__description" :field="fields?.description" />
          <a
            v-if="fields?.link?.value?.href"
            class="c-featured-resource-card__link"
            v-bind="getLinkAttrs(fields?.link)"
          >
            <span>{{ fields?.link?.value?.text || 'Learn More' }}</span>
            <span class="c-featured-resource-card__link-icon" aria-hidden="true"></span>
          </a>
        </div>
        <AdaptiveImage
          v-if="heroImage?.fields?.desktopImage?.value?.src"
          class="c-featured-resource-card__image"
          :field="heroImage"
          :is-background="true"
          :lazy-load="false"
          :enable-scroll-detection="false"
          :show="true"
          :show-image-redius="false"
        />
      </div>

      <div class="c-featured-resource-card__resource-bar">
        <h3 class="c-featured-resource-card__resource-heading">
          <span class="c-featured-resource-card__book-icon" aria-hidden="true"></span>
          <JssText :field="fields?.resourceHeading" />
        </h3>
        <span class="c-featured-resource-card__resource-count">{{ resourceCountLabel }}</span>
      </div>

      <div class="c-featured-resource-card__list">
        <article
          v-for="resource in resources"
          :key="resource.id || resource.name"
          class="c-featured-resource-card__item"
        >
          <div class="c-featured-resource-card__item-content">
            <p class="c-featured-resource-card__type">
              {{ resource.fields?.typeLabel?.value }}
            </p>
            <h4 class="c-featured-resource-card__item-title">
              {{ resource.fields ? getResourceTitle(resource.fields) : '' }}
            </h4>
            <ul v-if="resource.fields?.bullets?.length" class="c-featured-resource-card__bullets">
              <li
                v-for="bullet in resource.fields.bullets"
                :key="bullet.id || bullet.name"
                class="c-featured-resource-card__bullet"
              >
                {{ bullet.fields?.text?.value }}
              </li>
            </ul>
          </div>
          <a
            class="c-featured-resource-card__download"
            v-bind="resource.fields ? getLinkAttrs(resource.fields.downloadLink) : { href: '#' }"
            :aria-label="`Open ${resource.fields ? getResourceTitle(resource.fields) : 'resource'}`"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4.16699 10H15.8337"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke="white"
              />
              <path
                d="M10 4.16675L15.8333 10.0001L10 15.8334"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke="white"
              />
            </svg>
          </a>
        </article>
      </div>
    </ContentGrid>
  </section>
</template>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-featured-resource-card {
  width: 100%;
  padding: 32px 0;
  color: #111827;

  &__hero {
    position: relative;
    display: grid;
    overflow: hidden;
    border-radius: 18px;
    background: #6f9a78;
    box-shadow: 0 16px 30px rgba(17, 24, 39, 0.18);

    &::before {
      position: absolute;
      inset: 0 auto 0 0;
      z-index: 2;
      width: 100%;
      background: #6f9a78;
      content: '';
    }
  }

  &__hero-copy {
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 36px 0 36px 28px;
    color: $white;
  }

  &__title {
    max-width: 560px;
    margin: 0;
    color: $white;
    font-size: 28px;
    font-weight: 800;
    line-height: 1.16;
  }

  &__description {
    max-width: 560px;
    margin: 22px 0 0;
    color: rgba(255, 255, 255, 0.92);
    font-size: 14px;
    line-height: 1.56;

    p {
      margin: 0;
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 14px;
    margin-top: 30px;
    color: $white;
    font-size: 18px;
    font-weight: 800;
    line-height: 1;
    text-decoration: underline;
    text-underline-offset: 4px;

    &:hover {
      color: $white;
    }
  }

  &__link-icon {
    position: relative;
    width: 23px;
    height: 16px;

    &::before,
    &::after {
      position: absolute;
      content: '';
      background: currentColor;
    }

    &::before {
      top: 7px;
      left: 0;
      width: 22px;
      height: 2px;
      border-radius: 2px;
    }

    &::after {
      top: 3px;
      right: 0;
      width: 10px;
      height: 10px;
      border-top: 2px solid currentColor;
      border-right: 2px solid currentColor;
      background: transparent;
      transform: rotate(45deg);
    }
  }

  &__image {
    position: absolute;
    inset: 0 0 0 0;
    z-index: 1;

    > div,
    .background-image {
      width: 100%;
      height: 100%;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__resource-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 20px;
  }

  &__resource-heading {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    margin: 0;
    color: #111827;
    font-size: 20px;
    font-weight: 800;
    line-height: 1.2;
  }

  &__book-icon {
    position: relative;
    flex: 0 0 auto;
    width: 30px;
    height: 24px;

    &::before,
    &::after {
      position: absolute;
      top: 2px;
      width: 13px;
      height: 18px;
      border: 2px solid #6f9a78;
      border-radius: 2px 2px 4px 4px;
      content: '';
    }

    &::before {
      left: 1px;
      transform: skewY(4deg);
    }

    &::after {
      right: 1px;
      transform: skewY(-4deg);
    }
  }

  &__resource-count {
    color: #667085;
    font-size: 20px;
    line-height: 1;
    white-space: nowrap;
  }

  &__list {
    display: grid;
    gap: 16px;
    margin-top: 20px;
  }

  &__item {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
    border: 1px solid #dde3ea;
    border-radius: 18px;
    background: $white;
    padding: 24px;
  }

  &__item-content {
    min-width: 0;
  }

  &__type {
    display: inline-flex;
    align-items: center;
    min-height: 34px;
    margin: 0 0 18px;
    border-radius: 9px;
    background: #f1f5f2;
    padding: 0 14px;
    color: #6f9a78;
    font-size: 14px;
    font-weight: 800;
    line-height: 1;
    text-transform: uppercase;
  }

  &__item-title {
    margin: 0;
    color: #111827;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.28;
  }

  &__bullets {
    display: grid;
    gap: 11px;
    margin: 20px 0 0;
    padding: 0;
    list-style: none;
  }

  &__bullet {
    position: relative;
    margin: 0;
    padding-left: 36px;
    color: #667085;
    font-size: 14px;
    line-height: 1.4;

    &::before {
      position: absolute;
      top: 2px;
      left: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border: 1px solid #6f9a78;
      border-radius: 50%;
      color: #6f9a78;
      font-size: 14px;
      font-weight: 800;
      line-height: 18px;
      content: '';
    }

    &::after {
      position: absolute;
      top: 7px;
      left: 4px;
      width: 8px;
      height: 5px;
      border-bottom: 1px solid #6f9a78;
      border-left: 1px solid #6f9a78;
      content: '';
      transform: rotate(-45deg);
    }
  }

  &__download {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    justify-self: start;
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: #6f9a78;
    color: $white;
    box-shadow: 0 12px 18px rgba(17, 24, 39, 0.18);
    svg {
      width: 20px;
      height: 20px;
    }
  }
  @include tablet {
    padding: 44px 0;

    &__hero {
      &::before {
        width: 61%;
        clip-path: polygon(0 0, 100% 0, 78% 100%, 0 100%);
      }
    }

    &__image {
      left: 42%;
      width: 58%;
    }

    &__hero-copy {
      width: 48%;
      padding: 46px 0 46px 34px;
    }

    &__title {
      font-size: 36px;
    }

    &__description {
      font-size: 16px;
    }

    &__item {
      grid-template-columns: minmax(0, 1fr) 92px;
      align-items: end;
      padding: 24px;
    }

    &__download {
      justify-self: end;
    }
  }

  @include tablet-landscape {
    &__hero {
      border-radius: 22px;

      &::before {
        width: 60%;
        clip-path: polygon(0 0, 100% 0, 76% 100%, 0 100%);
      }
    }

    &__image {
      left: 43%;
      width: 57%;
    }

    &__hero-copy {
      width: 46%;
      padding: 50px 0 50px 48px;
    }

    &__title {
      font-size: 28px;
    }

    &__description {
      max-width: 590px;
      font-size: 16px;
    }
  }
}
</style>
